import {
  APICurrentUser,
  APIReferenceType,
  APIViewpointAttributes,
  LoadedState,
  PersonalSetting,
  Traversal,
  toRequestedPaths,
} from '@ardoq/api-types';
import type { INode } from '@ardoq/yfiles';
import { getReferenceTypeRepresentationDataFromReferenceType } from 'referenceRepresentationData';
import type { ReferenceTypeRepresentationData } from '@ardoq/graph';
import {
  ExecuteSavedViewpointPayload,
  ExecuteUnsavedViewpointPayload,
  ExecuteViewpointPayload,
} from './actions';
import { ContextShape } from '@ardoq/data-model';
import { isEmpty, isObject } from 'lodash';
import { confirmOpenNewDatasetOrSavedViewpointAndClosePreviousView } from '../viewpointNavigator/confirmOpenNewDatasetOrSavedViewpointAndClosePreviousView';
import { ViewpointBuilderState } from './types';
import { getComponentSelection } from './getComponentSelection';

export const getReferenceTypeRepresentationData = (
  type: APIReferenceType,
  displayLabel: string
): ReferenceTypeRepresentationData =>
  getReferenceTypeRepresentationDataFromReferenceType(type, displayLabel);

export const isContextNode = (node: INode) => node.tag.isContext();

export const isExecuteSavedViewpointPayload = (
  payload: ExecuteViewpointPayload
): payload is ExecuteSavedViewpointPayload => {
  return payload.type === 'EXECUTE_SAVED_VIEWPOINT';
};

export const isExecuteUnsavedViewpointPayload = (
  payload: ExecuteViewpointPayload | APIViewpointAttributes
): payload is ExecuteUnsavedViewpointPayload => {
  return (
    isObject(payload) &&
    'type' in payload &&
    (payload as ExecuteViewpointPayload).type === 'EXECUTE_UNSAVED_VIEWPOINT'
  );
};

export const isAnyWorkspaceOrLoadedStateOpen = (
  loadedStates: LoadedState[],
  context: ContextShape
) => {
  return !isEmpty(loadedStates) || !isEmpty(context.workspaceIds);
};

export const getIsSuppressOpenDatasetConfirmation = (
  user: APICurrentUser | null
): boolean =>
  user?.settings[PersonalSetting.DONT_SHOW_OPEN_DATASET_CONFIRMATION] ?? false;

export const confirmClosingPreviousViewIfOpen = async (
  loadedStates: LoadedState[],
  context: ContextShape,
  origin: 'openNewDataset' | 'openViewpoint' | 'openWorkspacesInViewpointMode',
  isSuppressConfirmDialog: boolean
) => {
  if (!isAnyWorkspaceOrLoadedStateOpen(loadedStates, context)) {
    return true;
  }
  return await confirmOpenNewDatasetOrSavedViewpointAndClosePreviousView({
    origin,
    isSuppressConfirmDialog,
  });
};

/**
 * This is only needed because TS is weird about the componentSelection type,
 * so it makes it difficult to extract the traversal from the state.
 */
export const extractTraversalParamsFromViewpointBuilderState = ({
  searchComponentsState,
  editTraversalsState,
}: ViewpointBuilderState): Traversal => {
  const {
    paths,
    filters,
    pathCollapsingRules,
    pathMatching,
    startSetExpansion,
    startSetExpansionFromType,
  } = editTraversalsState.stateAsSavableAttributes;
  const traversalBase = {
    paths: toRequestedPaths(paths, startSetExpansion),
    filters,
    pathCollapsingRules,
    pathMatching,
    // Only include when set, to avoid an explicit `undefined` key.
    ...(startSetExpansion ? { startSetExpansion } : {}),
    ...(startSetExpansion && startSetExpansionFromType
      ? { startSetExpansionFromType }
      : {}),
  };

  const componentSelection = getComponentSelection(searchComponentsState);
  return { ...traversalBase, componentSelection };
};
